// Копия данных этого браузера: профиль, избранное, туры и поездки, брони, история поиска, настройки, кошелёк.
// Файлом на устройство (вместе с файлами кошелька) или на сервер за аккаунтом Telegram (кошелёк — только описания):
// на новом телефоне подключите тот же Telegram и восстановите.
import { idb } from './idb';

/** Что входит в копию — данные путешественника. Не входят: id браузера, входы в кабинеты (ключи), служебные кэши */
export const BACKUP_KEYS = /^(profile|favorites|saved|travels|bookings|history|origin|currency|theme|lang|pax|trip|price|bannerClosed|visa:passport|planner(:[\w-]+)?|hotels:[\w-]+|trains:[\w-]+)$/;

/** Документ кошелька в копии; file.data — base64 (только в файле копии на устройстве) */
export interface BackupWalletDoc {
  id: string;
  title: string;
  kind: string;
  date: string | null;
  code: string;
  note: string;
  createdAt: string;
  fileName: string | null;
  file?: { name: string; type: string; data: string } | null;
}

export interface BackupFile {
  app: 'sayohat';
  v: 1;
  savedAt: string;
  device: string;
  keys: Record<string, string>;
  wallet: BackupWalletDoc[];
}

/** То, что умеет localStorage (в тестах — подмена) */
export interface KeyValue {
  readonly length: number;
  key(i: number): string | null;
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

export function collectKeys(s: KeyValue): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i);
    const v = k ? s.getItem(k) : null;
    if (k && v !== null && BACKUP_KEYS.test(k)) out[k] = v;
  }
  return out;
}

/** Записать ключи из копии (только разрешённые); число записанных */
export function restoreKeys(s: KeyValue, keys: Record<string, string>): number {
  let n = 0;
  for (const [k, v] of Object.entries(keys)) {
    if (!BACKUP_KEYS.test(k) || typeof v !== 'string') continue;
    s.setItem(k, v);
    n++;
  }
  return n;
}

/** Текст файла → копия; чужой файл — понятная ошибка */
export function parseBackup(text: string): BackupFile {
  let b: Partial<BackupFile> | null = null;
  try { b = JSON.parse(text) as Partial<BackupFile>; } catch { /* не JSON */ }
  if (!b || b.app !== 'sayohat' || !b.keys || typeof b.keys !== 'object') throw new Error('Это не файл копии Sayohat');
  return { app: 'sayohat', v: 1, savedAt: String(b.savedAt || ''), device: String(b.device || ''), keys: b.keys, wallet: Array.isArray(b.wallet) ? b.wallet : [] };
}

/** «Android, Chrome» — чтобы узнать копию в списке */
export function deviceName(ua: string): string {
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iPhone' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  const br = /Telegram/.test(ua) ? 'Telegram' : /YaBrowser/.test(ua) ? 'Яндекс Браузер' : /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '';
  return [os, br].filter(Boolean).join(', ');
}

/** Документ без файла: на новом устройстве подскажем, где остался файл */
export function walletNote(d: BackupWalletDoc): string {
  if (d.file || !d.fileName) return d.note;
  return [d.note, `Файл «${d.fileName}» остался на прежнем устройстве`].filter(Boolean).join('\n');
}

// ---------- Работа с браузером ----------

interface StoredDoc {
  id: string; title: string; kind: string; date: string | null; code: string; note: string; createdAt: string;
  file: { name: string; type: string; blob: Blob } | null;
}

const toBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
  r.onerror = () => reject(new Error('Не удалось прочитать файл'));
  r.readAsDataURL(blob);
});

function fromBase64(data: string, type: string): Blob {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

/** Собрать копию; withFiles — вместе с файлами кошелька (для файла на устройство) */
export async function makeBackup(withFiles: boolean): Promise<BackupFile> {
  const docs = await idb.all<StoredDoc>('wallet').catch(() => [] as StoredDoc[]);
  const wallet: BackupWalletDoc[] = [];
  for (const d of docs) {
    const file = withFiles && d.file ? { name: d.file.name, type: d.file.type, data: await toBase64(d.file.blob) } : null;
    wallet.push({ id: d.id, title: d.title, kind: d.kind, date: d.date, code: d.code, note: d.note, createdAt: d.createdAt, fileName: d.file?.name || null, file });
  }
  return { app: 'sayohat', v: 1, savedAt: new Date().toISOString(), device: deviceName(navigator.userAgent), keys: collectKeys(localStorage), wallet };
}

/** Восстановить копию в этом браузере: настройки и туры — заменяются, документы кошелька — добавляются */
export async function applyBackup(b: BackupFile): Promise<{ keys: number; docs: number }> {
  const keys = restoreKeys(localStorage, b.keys);
  const have = new Set((await idb.all<StoredDoc>('wallet').catch(() => [] as StoredDoc[])).map((d) => d.id));
  let docs = 0;
  for (const d of b.wallet) {
    if (!d?.id || have.has(d.id)) continue;
    const file = d.file?.data ? { name: d.file.name, type: d.file.type, blob: fromBase64(d.file.data, d.file.type) } : null;
    await idb.put<StoredDoc>('wallet', { id: d.id, title: d.title, kind: d.kind, date: d.date, code: d.code, note: walletNote(d), createdAt: d.createdAt, file });
    docs++;
  }
  persistStorage();
  return { keys, docs };
}

/** Скачать копию файлом */
export function downloadBackup(b: BackupFile): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([JSON.stringify(b)], { type: 'application/json' }));
  a.download = `sayohat-${b.savedAt.slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

let persistAsked = false;

/** Попросить браузер не удалять данные сайта сам (Safari стирает их через 7 дней без посещений).
 *  Вызывается после сохранения важного (документ в кошельке, копия), а не при загрузке: Firefox показывает вопрос. */
export function persistStorage(): void {
  if (persistAsked || typeof navigator === 'undefined' || !navigator.storage?.persist) return;
  persistAsked = true;
  void navigator.storage.persisted().then((on) => (on ? true : navigator.storage.persist())).catch(() => false);
}
